/* =============================================================================
 * LAS CORRECCIONES DEL CHECADOR (FASE 2b DE CHECADAS INALTERABLES)
 * -----------------------------------------------------------------------------
 * Ninguna checada se edita ni se borra jamás. El patrón CORRIGE con una anotación
 * que queda en `correcciones_checadas` (solo crece; ver firestore.rules, 8F-2):
 *   · "anular"       una checada deja de contar (sigue visible, atenuada);
 *   · "cambiar_tipo" una checada cuenta como otro tipo (entrada ↔ salida…);
 *   · "agregar"      un registro que faltó, con su hora declarada (hasta 60 días
 *                    atrás, nunca futura);
 *   · "revertir"     deshace una corrección anterior, con su propia anotación.
 * Cada una con motivo, nombre y cargo de quien la hace, su correo y la hora del
 * servidor. El trabajador puede dejar una nota "no conforme" sobre cualquier
 * corrección de sus registros (`no_conformidades`, 8F-3).
 *
 * AQUÍ VIVE LA VISTA EFECTIVA, UNA SOLA VEZ: lo que cuenta de las checadas de una
 * persona después de sus correcciones. La usan la ficha del patrón, la pantalla
 * del trabajador, la bitácora quincenal y el registro mensual; ninguna decide por
 * su cuenta qué cuenta y qué no.
 *
 * CommonJS, exportado nombre por nombre (como src/logica/checador.js): lo leen la
 * aplicación y las pruebas en Node.
 * ========================================================================== */
const { enHoraDeMexico } = require("./checador");

const TIPOS_DE_CORRECCION = ["agregar", "anular", "cambiar_tipo", "revertir"];
const TIPOS_DE_CHECADA = ["entrada", "salida_comer", "regreso_comer", "salida"];
// Las mismas palabras que `labelChecada` de App.tsx (una prueba lo vigila).
const ETIQUETA_TIPO = { entrada: "Entrada", salida_comer: "Salida a comer", regreso_comer: "Regreso de comer", salida: "Salida" };
const etiquetaTipo = (t) => ETIQUETA_TIPO[t] || "Salida";
// Lo que el papel y las pantallas dicen de cada tipo de corrección.
const NOMBRE_CORRECCION = { agregar: "Registro agregado", anular: "Anulación", cambiar_tipo: "Cambio de tipo", revertir: "Reversión" };

// Los límites, los mismos que las reglas (firestore.rules, 8F-2 y 8F-3).
const MINIMO_MOTIVO = 10;
const MAXIMO_MOTIVO = 500;
const MINIMO_FIRMA = 2;
const MAXIMO_FIRMA = 120;
const MINIMO_COMENTARIO = 10;
const MAXIMO_COMENTARIO = 1000;
const DIAS_PARA_AGREGAR = 60;
// Holgura contra la hora del servidor: el teléfono y el servidor no marcan lo
// mismo al segundo. Del lado seguro: se rechaza aquí antes que allá.
const HOLGURA_MS = 10 * 60 * 1000;

// ── Fechas ──────────────────────────────────────────────────────────────────
// Milisegundos de una marca de Firestore, de un Date o de un {seconds}. NaN si no.
function msDe(v) {
  if (!v) return NaN;
  if (typeof v.toMillis === "function") return v.toMillis();
  if (typeof v.getTime === "function") return v.getTime();
  if (typeof v.seconds === "number") return v.seconds * 1000 + Math.floor((Number(v.nanoseconds) || 0) / 1e6);
  return NaN;
}
// La hora oficial de una checada: la del servidor, y si no, `timestamp`.
function msDeChecada(c) {
  const a = msDe(c && c.horaServidor);
  return Number.isFinite(a) ? a : msDe(c && c.timestamp);
}
const _MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
// "9 de octubre de 2026 a las 08:10", en hora de México. "" si no hay fecha.
function fechaHoraMexico(ms) {
  if (!Number.isFinite(ms)) return "";
  const mx = enHoraDeMexico(new Date(ms));
  if (!mx) return "";
  return `${mx.getDate()} de ${_MESES[mx.getMonth()]} de ${mx.getFullYear()} a las ${String(mx.getHours()).padStart(2, "0")}:${String(mx.getMinutes()).padStart(2, "0")}`;
}
// Una marca de tiempo con la forma de Firestore, para el registro agregado (que
// no tiene checada propia): así lo leen igual horaDeChecada y msDeChecada.
function marcaDe(ms) {
  const d = new Date(ms);
  return { toDate: () => new Date(ms), toMillis: () => ms, seconds: Math.floor(ms / 1000), nanoseconds: (ms % 1000) * 1e6, getTime: () => d.getTime() };
}

// ── Quién, cuándo y por qué ─────────────────────────────────────────────────
function firmaDe(c) {
  const nombre = String((c && c.porNombre) || "").trim();
  const cargo = String((c && c.porCargo) || "").trim();
  return [nombre, cargo].filter(Boolean).join(", ") || String((c && c.por) || "").trim() || "el patrón";
}
const _quienYCuando = (c) => `${firmaDe(c)}, el ${fechaHoraMexico(msDe(c && c.en)) || "(fecha pendiente)"}`;
const _motivo = (c) => `Motivo: ${String((c && c.motivo) || "").trim()}`;

// ── La vista efectiva ────────────────────────────────────────────────────────
// Ordena las correcciones por su hora del servidor (`en`); una sin hora todavía
// (recién escrita en este navegador) cuenta como de ahora. Empates, por id.
function _ordenadas(correcciones) {
  const ahora = Date.now();
  return (correcciones || [])
    .filter((c) => c && typeof c === "object" && TIPOS_DE_CORRECCION.indexOf(c.tipo) >= 0)
    .map((c) => ({ c, ms: Number.isFinite(msDe(c.en)) ? msDe(c.en) : ahora }))
    .sort((a, b) => (a.ms - b.ms) || String(a.c.id || "").localeCompare(String(b.c.id || "")))
    .map((x) => x.c);
}
// El estado de cada corrección:
//   · "vigente"    cuenta;
//   · "revertida"  la deshizo una reversión (la PRIMERA que la revierte);
//   · "reversion"  una reversión que sí deshizo algo;
//   · "reversion_sin_efecto"  una reversión de algo que ya estaba revertido (las
//     reglas permiten revertir dos veces la misma corrección) o que no se
//     encuentra: no cambia nada, pero consta como lo que es.
function estadoDeCorrecciones(correcciones) {
  const lista = _ordenadas(correcciones);
  const porId = {}; lista.forEach((c) => { if (c.id) porId[c.id] = c; });
  const revertidaPor = {}, estado = {};
  for (const c of lista) {
    if (c.tipo !== "revertir") continue;
    const objetivo = porId[c.correccionId];
    if (!objetivo || objetivo.tipo === "revertir" || revertidaPor[objetivo.id]) { estado[c.id] = "reversion_sin_efecto"; continue; }
    revertidaPor[objetivo.id] = c; estado[c.id] = "reversion";
  }
  for (const c of lista) if (c.tipo !== "revertir") estado[c.id] = revertidaPor[c.id] ? "revertida" : "vigente";
  return { lista, porId, estado, revertidaPor };
}
// ¿Se puede revertir? Solo una corrección vigente que no sea a su vez reversión.
function sePuedeRevertir(correccion, estados) {
  return !!(correccion && correccion.tipo !== "revertir" && estados && estados.estado[correccion.id] === "vigente");
}

// LA VISTA EFECTIVA de las checadas de una persona:
//   checadas         las suyas (ya filtradas por persona);
//   correcciones     las de esa persona;
//   noConformidades  sus notas (opcional).
// Devuelve:
//   registros         uno por checada y uno por registro agregado, en orden de
//                     hora: tipo efectivo, si cuenta, y sus anotaciones;
//   correcciones      cada corrección con su estado, a qué registro afecta y sus
//                     notas "no conforme";
//   checadasQueCuentan  la lista que se califica: las checadas que cuentan (con su
//                     tipo efectivo) y los registros agregados vigentes, con la
//                     forma de una checada (y la marca `agregadoPorPatron`).
function vistaEfectiva(o) {
  const checadas = (o && o.checadas) || [];
  const est = estadoDeCorrecciones(o && o.correcciones);
  const notas = ((o && o.noConformidades) || []).slice().sort((a, b) => (msDe(a.en) || 0) - (msDe(b.en) || 0));
  const notasDe = (id) => notas.filter((n) => n && n.correccionId === id);
  const vigentes = (f) => est.lista.filter((c) => f(c) && est.estado[c.id] === "vigente");
  const ultima = (xs) => xs.length ? xs[xs.length - 1] : null;

  const registros = [];
  for (const ch of checadas) {
    const sobreEsta = (c) => c.checadaId === ch.id && (c.tipo === "anular" || c.tipo === "cambiar_tipo");
    const anulacion = ultima(vigentes((c) => sobreEsta(c) && c.tipo === "anular"));
    const cambio = ultima(vigentes((c) => sobreEsta(c) && c.tipo === "cambiar_tipo" && TIPOS_DE_CHECADA.indexOf(c.nuevoTipo) >= 0));
    registros.push({
      id: ch.id, origen: "original", checada: ch, ms: msDeChecada(ch),
      tipoRegistrado: ch.tipo, tipo: cambio ? cambio.nuevoTipo : ch.tipo,
      cuenta: !anulacion, anulacion, cambio,
      correcciones: est.lista.filter(sobreEsta),
    });
  }
  for (const c of est.lista.filter((x) => x.tipo === "agregar")) {
    const vigente = est.estado[c.id] === "vigente";
    registros.push({
      id: "agregado:" + c.id, origen: "agregado", checada: null, ms: msDe(c.horaDeclarada),
      tipoRegistrado: c.tipoChecada, tipo: c.tipoChecada, cuenta: vigente, agregado: c,
      revertido: !vigente, correcciones: [c],
    });
  }
  registros.sort((a, b) => (a.ms || 0) - (b.ms || 0));

  const registroDe = (c) => {
    if (!c) return null;
    if (c.tipo === "agregar") return registros.find((r) => r.id === "agregado:" + c.id) || null;
    if (c.tipo === "revertir") return registroDe(est.porId[c.correccionId]);
    return registros.find((r) => r.origen === "original" && r.id === c.checadaId) || null;
  };
  const correcciones = est.lista.map((c) => ({
    correccion: c, estado: est.estado[c.id],
    revertidaPor: est.revertidaPor[c.id] || null,
    revierte: c.tipo === "revertir" ? (est.porId[c.correccionId] || null) : null,
    registro: registroDe(c), noConformidades: notasDe(c.id),
  }));

  const checadasQueCuentan = registros.filter((r) => r.cuenta).map((r) => {
    if (r.origen === "original") {
      if (r.tipo === r.checada.tipo) return r.checada;
      return Object.assign({}, r.checada, { tipo: r.tipo, tipoRegistrado: r.checada.tipo, correccionDeTipo: r.cambio });
    }
    const m = marcaDe(r.ms);
    return { id: r.id, empleadorId: r.agregado.empleadorId, trabajadorEmail: r.agregado.trabajadorEmail, tipo: r.tipo,
      timestamp: m, horaServidor: m, agregadoPorPatron: true, correccion: r.agregado };
  });
  return { registros, correcciones, checadasQueCuentan, estados: est };
}

// ── Lo que se dice ───────────────────────────────────────────────────────────
// La anotación de un registro, como la leen el patrón y el trabajador.
function anotacionesDeRegistro(r) {
  const out = [];
  if (!r) return out;
  if (r.origen === "agregado") {
    out.push(`Agregado por ${_quienYCuando(r.agregado)}. ${_motivo(r.agregado)}`);
  }
  for (const c of r.correcciones || []) {
    if (c.tipo === "anular") out.push(`Anulado por ${_quienYCuando(c)}. ${_motivo(c)}`);
    if (c.tipo === "cambiar_tipo") out.push(`Registrado como ${etiquetaTipo(r.tipoRegistrado)}; corregido a ${etiquetaTipo(c.nuevoTipo)} por ${_quienYCuando(c)}. ${_motivo(c)}`);
  }
  return out;
}
// LAS ANOTACIONES DE UNA CORRECCIÓN, CADA UNA EN SU RENGLÓN (11-oct-2026): la
// corrección, y debajo, si se revirtió, quién la revirtió y por qué (o que la
// reversión no tuvo efecto). Iban pegadas en una sola cadena y en pantalla se leía
// "…Motivo: prueba plataforma Revertida por …", como si fuera parte del motivo.
// Quien la pinta pone cada elemento en su propio renglón.
function renglonesDeCorreccion(x) {
  const c = x.correccion;
  let base;
  if (c.tipo === "agregar") base = `${etiquetaTipo(c.tipoChecada)} agregada por ${_quienYCuando(c)}, con hora declarada del ${fechaHoraMexico(msDe(c.horaDeclarada))}. ${_motivo(c)}`;
  else if (c.tipo === "anular") base = `Anulado por ${_quienYCuando(c)}. ${_motivo(c)}`;
  else if (c.tipo === "cambiar_tipo") base = `Registrado como ${etiquetaTipo(x.registro ? x.registro.tipoRegistrado : "")}; corregido a ${etiquetaTipo(c.nuevoTipo)} por ${_quienYCuando(c)}. ${_motivo(c)}`;
  else base = `Corrección revertida por ${_quienYCuando(c)}. ${_motivo(c)}`;
  const out = [base];
  if (x.estado === "revertida" && x.revertidaPor) out.push(`Revertida por ${_quienYCuando(x.revertidaPor)}. ${_motivo(x.revertidaPor)}`);
  if (x.estado === "reversion_sin_efecto") out.push("Esa corrección ya estaba revertida: esta reversión no cambia nada.");
  return out;
}
// El registro afectado, en una línea: "Entrada del 3 de octubre de 2026 a las 09:05".
function registroAfectadoTexto(r) {
  if (!r) return "Registro no disponible";
  const cuando = fechaHoraMexico(r.ms);
  return `${etiquetaTipo(r.tipoRegistrado)} del ${cuando}${r.origen === "agregado" ? " (agregado por el patrón)" : ""}`;
}
// "El trabajador no está conforme: [texto], [fecha]" (la primera nota).
function noConformidadTexto(nota, paraElPatron) {
  if (!nota) return "";
  const t = String(nota.comentario || "").trim();
  return `${paraElPatron === false ? "Tu inconformidad" : "El trabajador no está conforme"}: ${t}, ${fechaHoraMexico(msDe(nota.en)) || "(fecha pendiente)"}`;
}

// ── El papel: "Correcciones del periodo" ─────────────────────────────────────
// UNA CORRECCIÓN PUEDE SALIR EN DOS DOCUMENTOS (decisión de Iván, 9-oct-2026). La
// bitácora de un periodo pasado normalmente ya se firmó y no se vuelve a firmar:
//   · en el del periodo del REGISTRO afectado ("Correcciones del periodo"), con la
//     marca "Hecha después del cierre del periodo, el …" si se hizo después;
//   · y en el del periodo en que SE HIZO, si es otro, aparte: "Correcciones hechas
//     en este periodo a registros de periodos anteriores". Ese apartado solo lista:
//     no cambia los conteos ni la calificación del periodo (cada registro cuenta en
//     el periodo de su fecha, nada más).
// Una fila del papel, con lo que consta y nada más.
function _filaCorreccion(x, hastaMs) {
  const c = x.correccion;
  const nota = x.noConformidades[0] || null;
  const enMs = msDe(c.en);
  const estado = x.estado === "revertida" ? `Revertida el ${fechaHoraMexico(msDe(x.revertidaPor.en))}`
    : x.estado === "reversion_sin_efecto" ? "Sin efecto: esa corrección ya estaba revertida"
    : x.estado === "reversion" ? `Revierte: ${NOMBRE_CORRECCION[x.revierte.tipo].toLowerCase()} del ${fechaHoraMexico(msDe(x.revierte.en))}`
    : "Vigente";
  return {
    cuando: fechaHoraMexico(enMs) || "(fecha pendiente)",
    tipo: NOMBRE_CORRECCION[c.tipo] + (c.tipo === "cambiar_tipo" ? ` (a ${etiquetaTipo(c.nuevoTipo)})` : ""),
    registro: registroAfectadoTexto(x.registro),
    autor: firmaDe(c),
    motivo: String(c.motivo || "").trim(),
    estado,
    noConformidad: nota ? `No conformidad del trabajador: ${String(nota.comentario || "").trim()}, ${fechaHoraMexico(msDe(nota.en))}` : "",
    despuesDelCierre: Number.isFinite(hastaMs) && Number.isFinite(enMs) && enMs > hastaMs ? `Hecha después del cierre del periodo, el ${fechaHoraMexico(enMs)}` : "",
  };
}
// Las correcciones de los registros del periodo [desdeMs, hastaMs] (por la hora
// del registro afectado; si no se encuentra, por la de la corrección). Sin
// correcciones, [] (y la sección no se imprime).
function correccionesDelPeriodo(vista, desdeMs, hastaMs) {
  return ((vista && vista.correcciones) || [])
    .filter((x) => {
      const ms = x.registro && Number.isFinite(x.registro.ms) ? x.registro.ms : msDe(x.correccion.en);
      return Number.isFinite(ms) && ms >= desdeMs && ms <= hastaMs;
    })
    .map((x) => _filaCorreccion(x, hastaMs));
}
// Las correcciones HECHAS en el periodo [desdeMs, hastaMs] a registros de periodos
// anteriores (el registro afectado es de antes de `desdeMs`). Solo se listan.
function correccionesHechasEnElPeriodo(vista, desdeMs, hastaMs) {
  return ((vista && vista.correcciones) || [])
    .filter((x) => {
      const en = msDe(x.correccion.en);
      return Number.isFinite(en) && en >= desdeMs && en <= hastaMs
        && !!x.registro && Number.isFinite(x.registro.ms) && x.registro.ms < desdeMs;
    })
    .map((x) => _filaCorreccion(x, NaN));
}
// La marca breve de un día en el renglón del papel (vacía si no hubo nada).
function marcaBreveDelDia(registrosDelDia) {
  const rs = registrosDelDia || [];
  const partes = [];
  const agregados = rs.filter((r) => r.origen === "agregado" && r.cuenta);
  if (agregados.length) partes.push(agregados.map((r) => etiquetaTipo(r.tipo).toLowerCase()).join(" y ") + " agregada" + (agregados.length > 1 ? "s" : "") + " por el patrón");
  const anulados = rs.filter((r) => r.origen === "original" && !r.cuenta).length;
  if (anulados) partes.push(`${anulados} registro${anulados > 1 ? "s" : ""} anulado${anulados > 1 ? "s" : ""} por el patrón`);
  const cambiados = rs.filter((r) => r.origen === "original" && r.cuenta && r.cambio).length;
  if (cambiados) partes.push(`${cambiados} registro${cambiados > 1 ? "s" : ""} con el tipo corregido por el patrón`);
  if (!partes.length) return "";
  const t = partes.join("; ");
  return t.charAt(0).toUpperCase() + t.slice(1);
}

// El día de México de un instante, "AAAA-MM-DD" (el mismo formato que los días
// del recorrido de la bitácora). "" si no hay fecha.
function claveDiaMexico(ms) {
  if (!Number.isFinite(ms)) return "";
  const mx = enHoraDeMexico(new Date(ms));
  return mx ? `${mx.getFullYear()}-${String(mx.getMonth() + 1).padStart(2, "0")}-${String(mx.getDate()).padStart(2, "0")}` : "";
}
// Los registros (de la vista efectiva) de un día de México.
function registrosDelDia(vista, claveDia) {
  return ((vista && vista.registros) || []).filter((r) => claveDiaMexico(r.ms) === claveDia);
}
// Cuántos días del periodo incluyen registros agregados por el patrón que cuentan.
function diasConRegistrosAgregados(vista, desdeMs, hastaMs) {
  const dias = {};
  ((vista && vista.registros) || []).forEach((r) => {
    if (r.origen === "agregado" && r.cuenta && r.ms >= desdeMs && r.ms <= hastaMs) dias[claveDiaMexico(r.ms)] = true;
  });
  return Object.keys(dias).length;
}
function textoDiasConAgregados(n) {
  return n ? `${n} ${n === 1 ? "día incluye registros agregados" : "días incluyen registros agregados"} por el patrón.` : "";
}

// ── Lo que se escribe ────────────────────────────────────────────────────────
const _texto = (v) => String(v == null ? "" : v).trim();
// Los datos de una corrección nueva, comprobados con los mismos límites que las
// reglas. Devuelve {datos} o {error}. `marca` es serverTimestamp() de quien
// escribe; `aTimestamp` convierte un Date en Timestamp de Firestore.
function datosDeCorreccion(o) {
  const motivo = _texto(o.motivo), nombre = _texto(o.nombre), cargo = _texto(o.cargo);
  if (TIPOS_DE_CORRECCION.indexOf(o.tipo) < 0) return { error: "Tipo de corrección desconocido." };
  if (motivo.length < MINIMO_MOTIVO) return { error: `Escribe el motivo (mínimo ${MINIMO_MOTIVO} caracteres).` };
  if (motivo.length > MAXIMO_MOTIVO) return { error: `El motivo puede tener hasta ${MAXIMO_MOTIVO} caracteres.` };
  if (nombre.length < MINIMO_FIRMA || nombre.length > MAXIMO_FIRMA) return { error: `Escribe el nombre de quien hace la corrección (${MINIMO_FIRMA} a ${MAXIMO_FIRMA} caracteres).` };
  if (cargo.length < MINIMO_FIRMA || cargo.length > MAXIMO_FIRMA) return { error: `Escribe el cargo de quien hace la corrección (${MINIMO_FIRMA} a ${MAXIMO_FIRMA} caracteres).` };
  const datos = { empleadorId: o.empleadorId, trabajadorEmail: String(o.trabajadorEmail || "").toLowerCase().trim(), tipo: o.tipo,
    motivo, por: o.correo, porNombre: nombre, porCargo: cargo, en: o.marca };
  if (o.tipo === "agregar") {
    if (TIPOS_DE_CHECADA.indexOf(o.tipoChecada) < 0) return { error: "Elige el tipo de registro." };
    const ms = msDe(o.horaDeclarada), ahora = Number.isFinite(o.ahoraMs) ? o.ahoraMs : Date.now();
    if (!Number.isFinite(ms)) return { error: "Escribe la fecha y la hora del registro." };
    if (ms > ahora) return { error: "La fecha y hora no pueden ser futuras." };
    if (ahora - ms > DIAS_PARA_AGREGAR * 86400000 - HOLGURA_MS) return { error: `Solo se pueden agregar registros de los últimos ${DIAS_PARA_AGREGAR} días.` };
    datos.tipoChecada = o.tipoChecada;
    datos.horaDeclarada = o.aTimestamp ? o.aTimestamp(new Date(ms)) : new Date(ms);
  } else if (o.tipo === "anular") {
    if (!o.checadaId) return { error: "Falta el registro que se anula." };
    datos.checadaId = o.checadaId;
  } else if (o.tipo === "cambiar_tipo") {
    if (!o.checadaId) return { error: "Falta el registro que se corrige." };
    if (TIPOS_DE_CHECADA.indexOf(o.nuevoTipo) < 0) return { error: "Elige el tipo correcto." };
    if (o.nuevoTipo === o.tipoRegistrado) return { error: "Elige un tipo distinto del registrado." };
    datos.checadaId = o.checadaId; datos.nuevoTipo = o.nuevoTipo;
  } else {
    if (!o.correccionId) return { error: "Falta la corrección que se revierte." };
    datos.correccionId = o.correccionId;
  }
  return { datos };
}
// Los datos de una nota "no conforme". Devuelve {datos} o {error}.
function datosDeNoConformidad(o) {
  const comentario = _texto(o.comentario);
  if (comentario.length < MINIMO_COMENTARIO) return { error: `Escribe por qué no estás conforme (mínimo ${MINIMO_COMENTARIO} caracteres).` };
  if (comentario.length > MAXIMO_COMENTARIO) return { error: `Puedes escribir hasta ${MAXIMO_COMENTARIO} caracteres.` };
  if (!o.correccionId) return { error: "Falta la corrección." };
  return { datos: { correccionId: o.correccionId, empleadorId: o.empleadorId, trabajadorEmail: String(o.trabajadorEmail || "").toLowerCase().trim(),
    comentario, por: o.correo, en: o.marca } };
}

const TEXTO_CONFIRMAR_CORRECCION = "Esta corrección quedará anotada con tu nombre, cargo, fecha y motivo, y se le avisará al trabajador. No se puede borrar; solo se puede revertir con otra corrección anotada.";
const TEXTO_CONFIRMAR_NO_CONFORME = "Tu inconformidad quedará anotada con fecha y no se puede borrar.";
const TEXTO_AVISO_CORRECCION = "Tu patrón corrigió un registro de tu checador. Revísalo.";

exports.TIPOS_DE_CORRECCION = TIPOS_DE_CORRECCION;
exports.TIPOS_DE_CHECADA = TIPOS_DE_CHECADA;
exports.ETIQUETA_TIPO = ETIQUETA_TIPO;
exports.NOMBRE_CORRECCION = NOMBRE_CORRECCION;
exports.MINIMO_MOTIVO = MINIMO_MOTIVO;
exports.MINIMO_FIRMA = MINIMO_FIRMA;
exports.MAXIMO_FIRMA = MAXIMO_FIRMA;
exports.MINIMO_COMENTARIO = MINIMO_COMENTARIO;
exports.DIAS_PARA_AGREGAR = DIAS_PARA_AGREGAR;
exports.msDe = msDe;
exports.msDeChecada = msDeChecada;
exports.fechaHoraMexico = fechaHoraMexico;
exports.firmaDe = firmaDe;
exports.estadoDeCorrecciones = estadoDeCorrecciones;
exports.sePuedeRevertir = sePuedeRevertir;
exports.vistaEfectiva = vistaEfectiva;
exports.anotacionesDeRegistro = anotacionesDeRegistro;
exports.renglonesDeCorreccion = renglonesDeCorreccion;
exports.registroAfectadoTexto = registroAfectadoTexto;
exports.noConformidadTexto = noConformidadTexto;
exports.correccionesDelPeriodo = correccionesDelPeriodo;
exports.correccionesHechasEnElPeriodo = correccionesHechasEnElPeriodo;
exports.marcaBreveDelDia = marcaBreveDelDia;
exports.claveDiaMexico = claveDiaMexico;
exports.registrosDelDia = registrosDelDia;
exports.diasConRegistrosAgregados = diasConRegistrosAgregados;
exports.textoDiasConAgregados = textoDiasConAgregados;
exports.datosDeCorreccion = datosDeCorreccion;
exports.datosDeNoConformidad = datosDeNoConformidad;
exports.TEXTO_CONFIRMAR_CORRECCION = TEXTO_CONFIRMAR_CORRECCION;
exports.TEXTO_CONFIRMAR_NO_CONFORME = TEXTO_CONFIRMAR_NO_CONFORME;
exports.TEXTO_AVISO_CORRECCION = TEXTO_AVISO_CORRECCION;
